@if (isFeatureActive) {
  <section class="global-search">
    <div class="global-search__search-input-wrapper">
      <input
        #searchInput
        class="global-search__search-input"
        type="search"
        [(ngModel)]="searchQuery"
        (focus)="onFocus()"
        (blur)="onBlur()"
        (input)="onSearchInput()"
        (keydown.escape)="onEscape()"
        (keydown)="onKeyDown($event)"
        [placeholder]="'GLOBAL_SEARCH.PLACEHOLDER' | translate"
        [attr.aria-label]="'GLOBAL_SEARCH.PLACEHOLDER' | translate"
        role="combobox"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        [attr.aria-expanded]="showResults"
        [attr.aria-controls]="'global-search-list'"
        [attr.aria-activedescendant]="activeOptionId"
        />
        @if (searchQuery.trim().length > 0) {
          <button
            type="button"
            class="global-search__clear-button"
            (click)="clearSearch()"
            [attr.aria-label]="'GLOBAL_SEARCH.CLEAR' | translate"
            [attr.aria-expanded]="showResults"
            >
            <span class="global-search__clear-button-icon">x</span>
          </button>
        }
      </div>
      @if (showResults && !showAll) {
        <div
          id="global-search-results"
          class="global-search__search-result-container"
          (mousedown)="$event.preventDefault()"
          (keydown.escape)="onEscape()"
          [attr.aria-label]="'GLOBAL_SEARCH.SEARCH_RESULTS' | translate"
          [class.global-search__search-result-container--has-results]="filteredResults.length > 0"
          >
          @if (filteredResults.length > 0 || searchQuery.trim().length >= 3) {
            <div
              class="global-search__categories"
              (mousedown)="$event.preventDefault()"
              >
              <button
                type="button"
                class="global-search__chip"
                [class.global-search__chip--active]="selectedCategory === 'all'"
                (click)="setCategory('all')"
                >
                {{ 'GLOBAL_SEARCH.ALL' | translate }}
                <span class="global-search__chip-count global-search__chip-count--auto">{{ formatCount(totalCount) }}</span>
              </button>
              @for (cat of categories; track cat) {
                <button
                  type="button"
                  class="global-search__chip"
                  [ngClass]="'global-search__chip--' + cat"
                  [class.global-search__chip--active]="selectedCategory === cat"
                  (click)="setCategory(cat)"
                  >
                  {{ getCategoryLabel(cat) }}
                  <span class="global-search__chip-count global-search__chip-count--auto">{{ formatCount(categoryCounts[cat] || 0) }}</span>
                </button>
              }
            </div>
          }
          @if (isLoading) {
            <div class="global-search__loading">Loading…</div>
          }
          @if (!isLoading && filteredResults.length > 0) {
            <ul
              #compactSearchList
              id="global-search-list"
              class="global-search__search-result-list"
              role="listbox"
              (scroll)="onCompactListScroll($event)"
              >
              @for (result of visibleResults; track trackById($index, result)) {
                <li
                  class="global-search__search-result-item"
                  [ngClass]="getItemClass(result.type)"
                  role="option"
                  [attr.id]="getOptionId(result)"
                  [class.is-active]="isActive(result)"
                  [attr.aria-selected]="isActive(result)"
                  tabindex="-1"
                  (click)="onResultClick(result)"
                  >
                  <div class="global-search__search-result-item-text-wrapper">
                    <span class="global-search__search-result-title">{{ result.header }}</span>
                    @if (getSubtitleRows(result).length > 0) {
                      <span class="global-search__search-result-description">
                        @for (row of getSubtitleRows(result); track row; let last = $last) {
                          <span class="global-search__search-result-subtitle-label">{{ getSubtitleLabel(row.key, result.type) }}:</span>
                          {{ row.value }}@if (!last) {
                          <span> · </span>
                        }
                      }
                    </span>
                  }
                  <span class="global-search__search-result-category">{{ getResultCategoryLabel(result) }}</span>
                </div>
              </li>
            }
          </ul>
        }
        @if (!isLoading && totalCount > 5) {
          <div
            class="global-search__expand-button-wrapper"
            [class.global-search__expand-button-wrapper--no-shadow]="!shouldShowCompactFooterShadow"
            >
            <button type="button" class="global-search__expand-button" (click)="showAllResults()">
              {{ 'GLOBAL_SEARCH.SHOW_ALL' | translate: { count: totalCount } }}
            </button>
          </div>
        }
        @if (canCollapse) {
          <div
            class="global-search__expand-button-wrapper"
            [class.global-search__expand-button-wrapper--no-shadow]="!shouldShowCompactFooterShadow"
            >
            <button type="button" class="global-search__expand-button" (click)="collapseResults()">
              {{ 'GLOBAL_SEARCH.SHOW_FEWER' | translate }}
            </button>
          </div>
        }
        @if (!isLoading && filteredResults.length === 0 && searchQuery.trim().length >= 3) {
          <div class="global-search__empty-state">
            {{ 'GLOBAL_SEARCH.EMPTY' | translate }}
          </div>
        }
      </div>
    }
    @if (showResults && showAll) {
      <div class="global-search__expanded-overlay" (scroll)="onExpandedOverlayScroll($event)">
        <div class="global-search__expanded-container">
          <button type="button" class="global-search__back-button" (click)="collapseResults()">
            ← {{ 'GLOBAL_SEARCH.BACK_TO_OVERVIEW' | translate }}
          </button>
          <div class="global-search__expanded-input-wrapper">
            <input
              #searchInput
              class="global-search__search-input"
              type="search"
              [(ngModel)]="searchQuery"
              (focus)="onFocus()"
              (blur)="onBlur()"
              (input)="onSearchInput()"
              (keydown.escape)="onEscape()"
              (keydown)="onKeyDown($event)"
              [placeholder]="'GLOBAL_SEARCH.PLACEHOLDER' | translate"
              [attr.aria-label]="'GLOBAL_SEARCH.PLACEHOLDER' | translate"
              role="combobox"
              aria-autocomplete="list"
              aria-haspopup="listbox"
              [attr.aria-expanded]="showResults"
              [attr.aria-controls]="'global-search-list-expanded'"
              [attr.aria-activedescendant]="activeOptionId"
              />
            </div>
            <h2 class="global-search__expanded-title">{{ 'GLOBAL_SEARCH.RESULTS_FOR' | translate: { query: searchQuery } }}</h2>
            <div class="global-search__expanded-subtitle">{{ 'GLOBAL_SEARCH.RESULTS_FOUND' | translate: { count: totalCount } }}</div>
            @if (isLoading) {
              <div class="global-search__loading">{{ 'GLOBAL_SEARCH.LOADING' | translate }}</div>
            }
            <!-- Full-bleed categories -->
            <div class="global-search__expanded-bleed">
              <div class="global-search__expanded-categories">
                <button
                  type="button"
                  class="global-search__chip"
                  [class.global-search__chip--active]="selectedCategory === 'all'"
                  (click)="setCategory('all')"
                  >
                  {{ 'GLOBAL_SEARCH.ALL' | translate }}
                  <span class="global-search__chip-count global-search__chip-count--auto">{{ formatCount(totalCount) }}</span>
                </button>
                @for (cat of categories; track cat) {
                  <button
                    type="button"
                    class="global-search__chip"
                    [ngClass]="'global-search__chip--' + cat"
                    [class.global-search__chip--active]="selectedCategory === cat"
                    (click)="setCategory(cat)"
                    >
                    {{ getCategoryLabel(cat) }}
                    <span class="global-search__chip-count global-search__chip-count--auto">{{ formatCount(categoryCounts[cat] || 0) }}</span>
                  </button>
                }
              </div>
            </div>
            @if (!isLoading && filteredResults.length > 0) {
              <ul
                id="global-search-list-expanded"
                class="global-search__search-result-list"
                role="listbox"
                >
                @for (result of expandedVisibleResults; track trackById($index, result)) {
                  <li
                    class="global-search__search-result-item"
                    [ngClass]="getItemClass(result.type)"
                    role="option"
                    [attr.id]="getOptionId(result)"
                    [class.is-active]="isActive(result)"
                    [attr.aria-selected]="isActive(result)"
                    tabindex="-1"
                    (click)="onResultClick(result)"
                    >
                    <div class="global-search__search-result-item-text-wrapper">
                      <span class="global-search__search-result-title">{{ result.header }}</span>
                      @if (getSubtitleRows(result).length > 0) {
                        <span class="global-search__search-result-description">
                          @for (row of getSubtitleRows(result); track row; let last = $last) {
                            <span class="global-search__search-result-subtitle-label">{{ getSubtitleLabel(row.key, result.type) }}:</span>
                            {{ row.value }}@if (!last) {
                            <span> · </span>
                          }
                        }
                      </span>
                    }
                    <span class="global-search__search-result-category">{{ getResultCategoryLabel(result) }}</span>
                  </div>
                </li>
              }
            </ul>
          }
          @if (!isLoading && expandedVisibleCount < filteredResults.length) {
            <div
              class="global-search__expand-button-wrapper"
              [class.global-search__expand-button-wrapper--no-shadow]="!shouldShowExpandedFooterShadow"
              >
              <button type="button" class="global-search__expand-button" (click)="loadMoreExpanded()">
                {{ 'GLOBAL_SEARCH.LOAD_MORE_WITH_REMAINING' | translate: { remaining: filteredResults.length - expandedVisibleCount } }}
              </button>
            </div>
          }
          @if (!isLoading && expandedVisibleCount >= filteredResults.length && filteredResults.length > 50) {
            <div
              class="global-search__expand-button-wrapper"
              [class.global-search__expand-button-wrapper--no-shadow]="!shouldShowExpandedFooterShadow"
              >
              <span>{{ 'GLOBAL_SEARCH.ALL_RESULTS_LOADED' | translate }}</span>
            </div>
          }
        </div>
      </div>
    }
  </section>
}
